import React, { useEffect, useMemo, useState } from "react";
import { V2_ROUTES } from "../state/useV2Navigation";
import { useV2Theme } from "../theme/ThemeProvider";
import { HELP_ARTICLES, HELP_SECTIONS, filterHelpArticles } from "../help/helpContent";
import { consumeHelpArticle, navigateHelpLink } from "../../utils/adminDeepLink";
import { v2cx } from "../theme/tokens";

const V2HelpPage = ({ onNavigate }) => {
  const { tokens } = useV2Theme();
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("menus");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const fromStash = consumeHelpArticle();
    if (!fromStash) return;
    const match = HELP_ARTICLES.find((a) => a.id === fromStash);
    if (!match) return;
    setSection(match.section);
    setOpenId(match.id);
  }, []);

  const filtered = useMemo(() => filterHelpArticles(query), [query]);

  const visible = useMemo(() => {
    if (query.trim()) return filtered;
    return filtered.filter((a) => a.section === section);
  }, [filtered, query, section]);

  const searching = Boolean(query.trim());

  const onCta = (nav) => {
    navigateHelpLink(onNavigate, V2_ROUTES, nav);
  };

  return (
    <section className="space-y-4">
      <div className="text-xs text-slate-500">
        <span>Workspace</span>
        <span className="mx-2 text-slate-300">/</span>
        <span className="text-slate-700">Help</span>
      </div>

      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Help</h2>
        <p className={`mt-1 ${tokens.surface.mutedText}`}>
          Menus, features, and product questions—kept separate from the Architecture
          assistant.
        </p>
      </div>

      <div className="relative max-w-xl">
        <label htmlFor="help-search" className="sr-only">
          Search Help
        </label>
        <input
          id="help-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search menus, SSO, CMDB…"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
        />
      </div>

      {!searching ? (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Help sections">
          {HELP_SECTIONS.map((s) => {
            const active = section === s.id;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSection(s.id)}
                className={v2cx(
                  tokens.button.chip,
                  active ? tokens.button.chipActive : tokens.button.chipIdle
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {!searching ? (
        <p className={`text-sm ${tokens.surface.subtleText}`}>
          {HELP_SECTIONS.find((s) => s.id === section)?.blurb}
        </p>
      ) : (
        <p className={`text-sm ${tokens.surface.subtleText}`}>
          {visible.length} result{visible.length === 1 ? "" : "s"} for “{query.trim()}”
        </p>
      )}

      <div className="space-y-2">
        {visible.length === 0 ? (
          <div className={`${tokens.surface.card} px-4 py-6`}>
            <p className={`text-sm ${tokens.surface.mutedText}`}>
              No matching articles. Try another term, or browse Using DecisionPen and
              Product &amp; integrations.
            </p>
          </div>
        ) : (
          visible.map((article) => {
            const open = openId === article.id;
            return (
              <div key={article.id} id={`help-${article.id}`} className={tokens.surface.card}>
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : article.id)}
                  className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
                >
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">
                      {article.title}
                    </span>
                    <span className={`mt-0.5 block text-xs ${tokens.surface.subtleText}`}>
                      {article.summary}
                    </span>
                  </span>
                  <span
                    className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                    aria-hidden
                  >
                    ▾
                  </span>
                </button>
                {open ? (
                  <div className="space-y-3 border-t border-slate-200 bg-slate-50/40 px-4 py-4">
                    {(article.body || []).map((para) => (
                      <p key={para.slice(0, 48)} className="text-sm text-slate-700">
                        {para}
                      </p>
                    ))}
                    {article.bullets?.length ? (
                      <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                        {article.bullets.map((b) => (
                          <li key={b.slice(0, 48)}>{b}</li>
                        ))}
                      </ul>
                    ) : null}
                    {article.note ? (
                      <p className="text-xs text-slate-500">{article.note}</p>
                    ) : null}
                    {article.ctas?.length ? (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {article.ctas.map((cta) => (
                          <button
                            key={`${article.id}-${cta.nav}`}
                            type="button"
                            onClick={() => onCta(cta.nav)}
                            className={tokens.button.secondary}
                          >
                            {cta.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
};

export default V2HelpPage;
